import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import {
  AttachMenu,
  AttachmentChip,
  Composer,
  ContextChip,
  DictationOverlay,
  HistoryPopover,
  MessageList,
  PoolPicker,
  SlashMenu,
  StartSuggestions,
  type ChatMode,
  type PoolItem,
  type Scope,
  type SlashItem,
  type Suggestion,
  type SuggestionAction,
  type UiMessage,
} from '@saip/ui'
import {
  SAMPLE_HISTORY,
  useBackend,
  useDictation,
  useHost,
  useHostAdapter,
  type UseChat,
} from '@saip/core'
import { scopeLabel } from '@saip/office'
import type { PromptTemplate, RagCollection, Skill } from '@saip/backend'

interface PoolRef {
  id: string
  name: string
}

type Overlay = 'none' | 'slash' | 'pool' | 'attach'

export interface ChatViewProps {
  chat: UseChat
  /** Owned by <App/>, so the list survives tab switches and loads only once. */
  prompts: PromptTemplate[]
  showHistory: boolean
  onCloseHistory: () => void
  /** A prompt command to drop into the composer (e.g. picked via "Verwenden"). */
  insert?: string | null
  onInsertConsumed?: () => void
}

export function ChatView({
  chat,
  prompts,
  showHistory,
  onCloseHistory,
  insert,
  onInsertConsumed,
}: ChatViewProps) {
  const backend = useBackend()
  const adapter = useHostAdapter()
  const host = useHost()

  const [text, setText] = useState('')
  const [scope, setScope] = useState<Scope>('document')
  const [mode, setMode] = useState<ChatMode>('fast')
  const [overlay, setOverlay] = useState<Overlay>('none')
  const [slashQuery, setSlashQuery] = useState('')
  // Inline slash autocomplete: keyboard-highlighted row + Escape-dismiss state.
  const [slashIndex, setSlashIndex] = useState(0)
  const [slashDismissed, setSlashDismissed] = useState(false)
  const [refs, setRefs] = useState<PoolRef[]>([])
  const [attachment, setAttachment] = useState<{ name: string; size: string } | null>(null)

  const [skills, setSkills] = useState<Skill[]>([])
  const [pools, setPools] = useState<RagCollection[]>([])

  const dictation = useDictation((final) => setText(final))
  const fileRef = useRef<HTMLInputElement>(null)

  // Independent loads: one failing or slow call must not hold back the others.
  useEffect(() => {
    let active = true
    backend.skills
      .list()
      .then((s) => active && setSkills(s))
      .catch(() => {})
    backend.rag
      .listCollections()
      .then((c) => active && setPools(c))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [backend])

  // Insert a prompt command into the composer when one is picked elsewhere.
  useEffect(() => {
    if (!insert) return
    setText((t) => (t ? `${t}${insert} ` : `${insert} `))
    onInsertConsumed?.()
  }, [insert, onInsertConsumed])

  const exampleChips: Suggestion[] = prompts.map((p) => ({
    command: p.command ?? `/${p.id}`,
    tone: 'prompt' as const,
  }))

  const slashSkills: SlashItem[] = skills
    .filter((s) => s.enabled)
    .map((s) => ({ command: s.command, description: s.description, source: s.source }))
  const slashPrompts: SlashItem[] = prompts
    .filter((p) => p.command)
    .map((p) => ({
      command: p.command as string,
      description: p.description ?? '',
      source: p.source,
    }))
  const poolItems: PoolItem[] = pools.map((p) => ({
    id: p.id,
    name: p.name,
    documentCount: p.documentCount,
    status: p.status,
  }))

  const messages: UiMessage[] = chat.turns

  const handleSend = () => {
    const value = text.trim()
    if (!value) return
    // Resolve a leading prompt command (e.g. "/kuerzer-fassen") to its template,
    // so the model receives the prompt body — not the literal command.
    const first = value.split(/\s+/)[0] ?? ''
    const matched = prompts.find((p) => (p.command ?? `/${p.id}`) === first)
    const rest = matched ? value.slice(first.length).trim() : value
    setText('')
    setOverlay('none')
    void chat.send(rest, {
      scope,
      mode,
      collectionId: refs[0]?.id,
      promptBody: matched?.body,
      promptLabel: matched?.name,
    })
  }

  // ── Inline slash autocomplete ───────────────────────────────
  // The menu opens while the composer holds a leading command token ("/…" with no
  // space yet) and closes once a space is typed or the user dismisses it (Esc).
  const slashToken = /^\/\S*$/.test(text) ? text : null
  const slashOpen = slashToken !== null && !slashDismissed && !dictation.active
  const slashSkillMatches = slashOpen ? filterItems(slashSkills, slashToken ?? '') : []
  const slashPromptMatches = slashOpen ? filterItems(slashPrompts, slashToken ?? '') : []
  const slashMatches = [...slashSkillMatches, ...slashPromptMatches]
  const slashActiveIndex = slashMatches.length ? Math.min(slashIndex, slashMatches.length - 1) : 0
  const slashActiveCommand = slashMatches[slashActiveIndex]?.command

  // Reset the highlight whenever the query changes or the menu (re)opens.
  useEffect(() => {
    setSlashIndex(0)
  }, [slashToken, slashOpen])

  // Re-arm the menu once the leading command token is gone, so the next "/" reopens it.
  useEffect(() => {
    if (slashToken === null) setSlashDismissed(false)
  }, [slashToken])

  // Autocomplete: replace the whole leading token with the picked command.
  const pickSlashInline = (command: string) => {
    setText(`${command} `)
    setSlashIndex(0)
    setSlashDismissed(false)
  }

  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!slashOpen) return false
    if (e.key === 'Escape') {
      e.preventDefault()
      setSlashDismissed(true)
      return true
    }
    if (slashMatches.length === 0) return false
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSlashIndex((i) => (i + 1) % slashMatches.length)
        return true
      case 'ArrowUp':
        e.preventDefault()
        setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length)
        return true
      case 'Enter':
      case 'Tab': {
        e.preventDefault()
        const item = slashMatches[slashActiveIndex]
        if (item) pickSlashInline(item.command)
        return true
      }
      default:
        return false
    }
  }

  const toggleMic = async () => {
    if (dictation.active) {
      // While the recording is being transcribed there is nothing left to stop.
      if (!dictation.transcribing) await dictation.stop()
      return
    }
    setText('')
    await dictation.start()
  }

  const onAction = async (message: UiMessage, action: SuggestionAction) => {
    try {
      if (action === 'copy') await navigator.clipboard?.writeText(message.content)
      else if (action === 'replace') await adapter.replaceSelection(message.content, 'markdown')
      else if (action === 'insert') await adapter.insertAtCursor(message.content, 'markdown')
    } catch {
      /* no-op in the browser fallback */
    }
  }

  const addRef = (id: string) => {
    const pool = pools.find((p) => p.id === id)
    if (pool && !refs.some((r) => r.id === id)) setRefs([...refs, { id, name: pool.name }])
    setOverlay('none')
  }

  const chips =
    refs.length > 0 || attachment ? (
      <>
        {attachment && (
          <AttachmentChip
            name={attachment.name}
            size={attachment.size}
            note="nur dieser Prompt"
            onRemove={() => setAttachment(null)}
          />
        )}
        {refs.map((r) => (
          <ContextChip
            key={r.id}
            label={`@${r.name}`}
            kind="pool"
            onRemove={() => setRefs(refs.filter((x) => x.id !== r.id))}
          />
        ))}
      </>
    ) : undefined

  return (
    <>
      {showHistory && (
        <HistoryPopover
          items={SAMPLE_HISTORY.map((h) => ({ id: h.id, title: h.title, tokens: h.tokens }))}
          onSelect={onCloseHistory}
        />
      )}

      {dictation.active ? (
        <DictationOverlay
          state={dictation.transcribing ? 'transcribing' : 'listening'}
          onStop={() => void dictation.stop()}
        />
      ) : messages.length === 0 ? (
        <StartSuggestions favorites={exampleChips} recent={[]} onPick={(c) => setText(`${c} `)} />
      ) : (
        <MessageList messages={messages} onAction={onAction} canEdit={adapter.canEdit} />
      )}

      <div className="relative">
        {(slashOpen || overlay !== 'none') && !dictation.active && (
          <div className="absolute bottom-full left-0 right-0 z-20 px-3 pb-2">
            {slashOpen ? (
              // Inline autocomplete — driven by the "/…" typed in the composer.
              <SlashMenu
                hideSearch
                query={slashToken ?? ''}
                onQuery={() => {}}
                skills={slashSkillMatches}
                prompts={slashPromptMatches}
                activeCommand={slashActiveCommand}
                onPick={pickSlashInline}
              />
            ) : overlay === 'slash' ? (
              <SlashMenu
                query={slashQuery}
                onQuery={setSlashQuery}
                skills={filterItems(slashSkills, slashQuery)}
                prompts={filterItems(slashPrompts, slashQuery)}
                onPick={(c) => {
                  setText((t) => (t.endsWith('/') ? `${t.slice(0, -1)}${c} ` : `${t}${c} `))
                  setOverlay('none')
                }}
              />
            ) : overlay === 'pool' ? (
              <PoolPicker pools={poolItems} onPick={addRef} />
            ) : overlay === 'attach' ? (
              <AttachMenu
                onUpload={() => fileRef.current?.click()}
                onPool={() => setOverlay('pool')}
                onSlash={() => setOverlay('slash')}
                onScreenshot={() => setOverlay('none')}
              />
            ) : null}
          </div>
        )}

        {dictation.error && (
          <div className="px-3 pb-2">
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <span className="flex-1">{dictation.error}</span>
              <button
                type="button"
                onClick={dictation.clearError}
                aria-label="Hinweis schließen"
                className="font-semibold text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <Composer
          value={dictation.active ? dictation.transcript : text}
          onChange={setText}
          onSend={handleSend}
          onInputKeyDown={onComposerKeyDown}
          scope={scope}
          onScopeChange={setScope}
          mode={mode}
          onModeChange={setMode}
          busy={chat.busy}
          onStop={chat.stop}
          onMic={() => void toggleMic()}
          dictating={dictation.active}
          onPlus={() => setOverlay((o) => (o === 'attach' ? 'none' : 'attach'))}
          onSlash={() => setOverlay((o) => (o === 'slash' ? 'none' : 'slash'))}
          placeholder={messages.length > 0 ? 'Antworten…' : 'Nachricht an Text Assistent…'}
          scopeDocumentLabel={scopeLabel(host, 'document')}
          chips={chips}
        />
      </div>

      <input
        ref={fileRef}
        type="file"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) setAttachment({ name: file.name, size: `${Math.round(file.size / 1024)} KB` })
          setOverlay('none')
        }}
      />
    </>
  )
}

function filterItems(items: SlashItem[], q: string): SlashItem[] {
  const query = q.replace(/^\//, '').toLowerCase().trim()
  if (!query) return items
  return items.filter(
    (i) => i.command.toLowerCase().includes(query) || i.description.toLowerCase().includes(query),
  )
}
